/**
 * Projects Tab Component for Admin Panel
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  FolderOpen, Clock, CheckCircle, AlertCircle, Loader2, ArrowLeft,
  Calendar, ExternalLink, RefreshCw, Inbox, DollarSign, Plus, TestTube,
  X, ChevronRight, Send, Link, Copy, CreditCard
} from 'lucide-react';
import { getStatusBadge, getPlanBadge } from './adminHelpers';

export const ProjectsTab = ({
  // State
  projects,
  projectStats,
  selectedProject,
  projectDetails,
  loadingDetails,
  statusFilter,
  setStatusFilter,
  updatingStatus,
  reviewers,
  userRole,
  showCreateForCustomer,
  setShowCreateForCustomer,
  customerEmail,
  setCustomerEmail,
  generatingPaymentLink,
  sendingPaymentLink,
  // Sorting
  sortConfig,
  handleSort,
  // Actions
  startCreateForCustomer,
  fetchProjectDetails,
  updateProjectStatus,
  assignProject,
  qaSkipPayment,
  generatePaymentLink,
  sendPaymentLink,
  copyPaymentLink
}) => {
  // Sort and filter projects
  const sortData = (data) => {
    if (!sortConfig?.key || !data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested business_info
      if (sortConfig.key === 'business_name') {
        aVal = a.business_info?.business_name || '';
        bVal = b.business_info?.business_name || '';
      }
      if (sortConfig.key === 'customer_email') {
        aVal = a.customer_email || a.user_email || '';
        bVal = b.customer_email || b.user_email || '';
      }
      
      // Handle dates
      if (sortConfig.key.includes('date') || sortConfig.key.includes('created_at') || sortConfig.key.includes('_at')) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle strings
      else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredProjects = statusFilter === 'all' 
    ? projects 
    : projects.filter(p => p.status === statusFilter);
  
  const sortedProjects = sortData(filteredProjects);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th 
      className={`text-left py-3 px-3 font-medium text-zinc-600 cursor-pointer hover:bg-zinc-100 select-none ${className}`}
      onClick={() => handleSort && handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig?.key === sortKey && (
          <span className="text-amber-500 text-xs">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  return (
    <>
      {/* Create for Customer Form */}
      {showCreateForCustomer && (
        <Card className="mb-6 border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create Project for Customer
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreateForCustomer(false); setCustomerEmail(''); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-600 mb-4">
              Enter the customer&apos;s email address. You&apos;ll go through the full briefing flow to create their design.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input 
                  placeholder="customer@email.com" 
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && startCreateForCustomer()}
                />
              </div>
              <Button onClick={startCreateForCustomer}>
                <ArrowLeft className="w-4 h-4 mr-2 rotate-180" /> Start Briefing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {[
          { label: 'Total', value: projectStats.total || 0, color: 'border-zinc-500', textColor: 'text-zinc-700', icon: FolderOpen },
          { label: 'Pending', value: projectStats.pending_review || 0, color: 'border-amber-500', textColor: 'text-amber-700', icon: Clock },
          { label: 'In Progress', value: projectStats.in_progress || 0, color: 'border-blue-500', textColor: 'text-blue-700', icon: RefreshCw },
          { label: 'Finalized', value: projectStats.finalized || 0, color: 'border-green-500', textColor: 'text-green-700', icon: CheckCircle },
          { label: 'Rejected', value: projectStats.rejected || 0, color: 'border-red-500', textColor: 'text-red-700', icon: AlertCircle },
          { label: 'This Week', value: projectStats.this_week || 0, color: 'border-purple-500', textColor: 'text-purple-700', icon: Calendar },
          { label: 'Revenue', value: `₹${(projectStats.revenue?.total_inr || 0).toLocaleString()}`, color: 'border-emerald-500', textColor: 'text-emerald-700', icon: DollarSign }
        ].map((stat, i) => (
          <Card key={i} className={`border-l-4 ${stat.color} bg-white`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs">{stat.label}</p>
                  <p className={`text-xl font-bold ${stat.textColor}`}>{stat.value}</p>
                </div>
                <stat.icon className={`w-6 h-6 ${stat.textColor} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Projects Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Projects Table */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">All Projects</CardTitle>
                <div className="flex items-center gap-2">
                  {(userRole === 'admin' || userRole === 'design_reviewer') && (
                    <Button size="sm" onClick={() => setShowCreateForCustomer(true)}>
                      <Plus className="w-4 h-4 mr-1" /> Create for Customer
                    </Button>
                  )}
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs border rounded px-2 py-1">
                    <option value="all">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="in_progress">In Progress</option>
                    <option value="finalized">Finalized</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredProjects.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Inbox className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p>No projects found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <SortableHeader label="Business" sortKey="business_name" />
                        <SortableHeader label="Customer" sortKey="customer_email" />
                        <SortableHeader label="Status" sortKey="status" />
                        <SortableHeader label="Plan" sortKey="plan" />
                        <th className="text-left py-3 px-3 font-medium text-zinc-600">Assigned</th>
                        <SortableHeader label="Date" sortKey="created_at" />
                        <th className="text-left py-3 px-3 font-medium text-zinc-600"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProjects.filter(p => !p.is_sub_project).map(project => (
                        <React.Fragment key={project.project_id}>
                          <tr 
                            className={`border-b border-zinc-100 hover:bg-amber-50 cursor-pointer transition-colors ${selectedProject === project.project_id ? 'bg-amber-50' : 'bg-white'}`}
                            onClick={() => fetchProjectDetails(project.project_id)}
                          >
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-zinc-900 truncate max-w-[150px]">{project.business_info?.business_name || 'Unnamed'}</div>
                                {project.sub_project_count > 0 && (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    +{project.sub_project_count} sub
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-zinc-500">{project.business_info?.business_type}</div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-xs text-zinc-600 truncate max-w-[120px]">{project.user_email}</div>
                            </td>
                            <td className="py-3 px-3">{getStatusBadge(project.status)}</td>
                            <td className="py-3 px-3">{getPlanBadge(project.freeze_plan)}</td>
                            <td className="py-3 px-3">
                              <span className="text-xs text-zinc-500 truncate max-w-[100px] block">
                                {project.assigned_to || '-'}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-xs text-zinc-500">
                              {project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-3 px-3">
                              <ChevronRight className="w-4 h-4 text-zinc-400" />
                            </td>
                          </tr>
                          {/* Sub-projects rows */}
                          {project.sub_projects?.map(sub => (
                            <tr 
                              key={sub.project_id}
                              className={`border-b border-zinc-100 hover:bg-purple-50 cursor-pointer transition-colors ${selectedProject === sub.project_id ? 'bg-purple-50' : 'bg-zinc-50/50'}`}
                              onClick={() => fetchProjectDetails(sub.project_id)}
                            >
                              <td className="py-2 px-3 pl-8">
                                <div className="flex items-center gap-2">
                                  <span className="text-purple-400">↳</span>
                                  <div className="font-medium text-zinc-700 text-xs">{sub.business_name}</div>
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">sub</span>
                                </div>
                              </td>
                              <td className="py-2 px-3 text-xs text-zinc-400">—</td>
                              <td className="py-2 px-3">{getStatusBadge(sub.status)}</td>
                              <td className="py-2 px-3 text-xs text-zinc-400">—</td>
                              <td className="py-2 px-3 text-xs text-zinc-400">—</td>
                              <td className="py-2 px-3 text-xs text-zinc-400">
                                {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="py-2 px-3">
                                <ChevronRight className="w-3 h-3 text-zinc-300" />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Project Details Panel */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {selectedProject ? 'Project Details' : 'Select a project'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                </div>
              ) : projectDetails ? (
                <ProjectDetailsPanel 
                  projectDetails={projectDetails}
                  userRole={userRole}
                  updatingStatus={updatingStatus}
                  reviewers={reviewers}
                  sendingPaymentLink={sendingPaymentLink}
                  generatingPaymentLink={generatingPaymentLink}
                  updateProjectStatus={updateProjectStatus}
                  assignProject={assignProject}
                  qaSkipPayment={qaSkipPayment}
                  copyPaymentLink={copyPaymentLink}
                  sendPaymentLink={sendPaymentLink}
                  generatePaymentLink={generatePaymentLink}
                  fetchProjectDetails={fetchProjectDetails}
                />
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <FolderOpen className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                  <p className="text-sm">Click a project to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

// Project Details Panel Sub-component
const ProjectDetailsPanel = ({
  projectDetails,
  userRole,
  updatingStatus,
  reviewers,
  sendingPaymentLink,
  generatingPaymentLink,
  updateProjectStatus,
  assignProject,
  qaSkipPayment,
  copyPaymentLink,
  sendPaymentLink,
  generatePaymentLink,
  fetchProjectDetails
}) => (
  <div className="space-y-4">
    <div className="p-3 bg-zinc-50 rounded-lg">
      <p className="font-medium text-zinc-900">{projectDetails.business_info?.business_name}</p>
      <p className="text-xs text-zinc-500">{projectDetails.business_info?.business_type}</p>
      <p className="text-xs text-zinc-600 mt-2 line-clamp-2">{projectDetails.business_info?.business_description}</p>
    </div>
    
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div>
        <p className="text-zinc-500">Customer</p>
        <p className="font-medium truncate">{projectDetails.user_email}</p>
      </div>
      <div>
        <p className="text-zinc-500">Pages</p>
        <p className="font-medium">{projectDetails.designs?.[0]?.pages?.length || 0}</p>
      </div>
      <div>
        <p className="text-zinc-500">Payment</p>
        <p className="font-medium">{projectDetails.payment_status || 'N/A'}</p>
      </div>
      <div>
        <p className="text-zinc-500">Frozen</p>
        <p className="font-medium">{projectDetails.frozen_at ? new Date(projectDetails.frozen_at).toLocaleDateString() : 'No'}</p>
      </div>
    </div>
    
    {/* Status Update */}
    <div className="border-t pt-4">
      <p className="text-xs text-zinc-500 mb-2">Update Status</p>
      <select
        value={projectDetails.status}
        onChange={(e) => updateProjectStatus(projectDetails.project_id, e.target.value)}
        disabled={updatingStatus}
        className="w-full text-sm border rounded px-3 py-2"
      >
        <option value="draft">Draft</option>
        <option value="pending_review">Pending Review</option>
        <option value="in_progress">In Progress</option>
        <option value="finalized">Finalized</option>
        <option value="rejected">Rejected</option>
      </select>
    </div>
    
    {/* Assignment */}
    {userRole === 'admin' && reviewers.length > 0 && (
      <div>
        <p className="text-xs text-zinc-500 mb-2">Assign to Reviewer</p>
        <select
          value={projectDetails.assigned_to || ''}
          onChange={(e) => assignProject(projectDetails.project_id, e.target.value)}
          className="w-full text-sm border rounded px-3 py-2"
        >
          <option value="">Unassigned</option>
          {reviewers.map(r => (
            <option key={r.member_email} value={r.member_email}>{r.name || r.member_email}</option>
          ))}
        </select>
      </div>
    )}
    
    {/* QA Skip Payment */}
    {(userRole === 'admin' || userRole === 'qa') && projectDetails.payment_status !== 'paid' && projectDetails.freeze_plan && projectDetails.freeze_plan !== 'save_only' && (
      <Button
        variant="outline"
        size="sm"
        className="w-full border-orange-200 text-orange-600 hover:bg-orange-50"
        onClick={() => qaSkipPayment(projectDetails.project_id)}
      >
        <TestTube className="w-4 h-4 mr-2" /> Skip Payment (QA)
      </Button>
    )}
    
    {/* Payment Link Section */}
    {projectDetails.created_by_admin && projectDetails.payment_status !== 'paid' && (
      <div className="border-t pt-4">
        <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
          <CreditCard className="w-3 h-3" /> Payment Link
        </p>
        
        {projectDetails.payment_token ? (
          <div className="space-y-2">
            <div className="p-2 bg-zinc-50 rounded text-xs break-all">
              {`${window.location.origin}/pay/${projectDetails.payment_token}`}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => copyPaymentLink(`${window.location.origin}/pay/${projectDetails.payment_token}`)}
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
              <Button 
                size="sm" 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => sendPaymentLink(projectDetails.project_id)}
                disabled={sendingPaymentLink}
              >
                {sendingPaymentLink ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Send
              </Button>
            </div>
            {projectDetails.payment_link_sent && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Link sent to customer
              </p>
            )}
          </div>
        ) : (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => generatePaymentLink(projectDetails.project_id, 'full_service', projectDetails.designs?.[0]?.pages?.length || 4)}
            disabled={generatingPaymentLink}
          >
            {generatingPaymentLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link className="w-4 h-4 mr-2" />}
            Generate Payment Link
          </Button>
        )}
      </div>
    )}
    
    {/* Sub-projects section */}
    {projectDetails.sub_projects?.length > 0 && (
      <div className="border-t pt-4">
        <p className="text-xs text-zinc-500 mb-2">Sub-projects ({projectDetails.sub_projects.length})</p>
        <div className="space-y-1">
          {projectDetails.sub_projects.map(sub => (
            <div 
              key={sub.project_id}
              className="flex items-center justify-between p-2 bg-purple-50 rounded text-xs cursor-pointer hover:bg-purple-100"
              onClick={() => fetchProjectDetails(sub.project_id)}
            >
              <span className="text-purple-700">{sub.business_name}</span>
              {getStatusBadge(sub.status)}
            </div>
          ))}
        </div>
      </div>
    )}
    
    {/* Parent project link */}
    {projectDetails.parent_project_id && (
      <div className="border-t pt-4">
        <p className="text-xs text-zinc-500 mb-1">Parent Project</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full text-purple-600 border-purple-200"
          onClick={() => fetchProjectDetails(projectDetails.parent_project_id)}
        >
          View Parent Project
        </Button>
      </div>
    )}
    
    <Button
      className="w-full bg-amber-600 hover:bg-amber-700"
      onClick={() => window.open(`/preview/${projectDetails.project_id}`, '_blank')}
    >
      <ExternalLink className="w-4 h-4 mr-2" /> View Design
    </Button>
  </div>
);

export default ProjectsTab;
